import { InvestmentDetailsCard } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/components/InvestmentDetailsCard/InvestmentDetailsCard.tsx';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { InvestmentPlaceholder } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/components/InvestmentPlaceholder/InvestmentPlaceholder.tsx';
import { Button, Stack } from '@mantine/core';
import { CardBodySkeleton } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/components/InvestmentDetailsCard/CardBodySkeleton.tsx';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';
import {
	InvestmentDetailsTaxComplianceItem,
	InvestmentDetailsTaxComplianceStatus,
} from '@dealstack/shared/lib/generated';
import { InvestmentDocumentCard } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/components/InvestmentDetailsContent/components/InvestmentDocumentCard/InvestmentDocumentCard.tsx';
import { InvestmentDocumentCardProps } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/components/InvestmentDetailsContent/components/InvestmentDocumentCard/InvestmentDocumentCard.types.ts';
import { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.types.ts';
import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n.ts';
import { resolveStatusBadgeColors } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageInvestments/utils/statusStyling.helper.ts';
import { useInvestmentDetailsTaxCompliance } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/hooks/useInvestmentDetailsTaxCompliance.ts';
import { PermissionWrapper } from '@dealstack/frontend/shared/components/PermissionWrapper/PermissionWrapper';
import {
	PermissionRole,
	ResourceType,
} from '@dealstack/shared/lib/common/fineGrainedAuthorization/permissions';
import { defaultToEmptyString } from '@dealstack/shared/lib/format/string';
import { useParams } from 'react-router-dom';
import { ManageRequirementsButton } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/components/InvestmentDetailsCard/ManageRequirementsButton.tsx';

const TAX_COMPLIANCE_STATUS_COLOR: Record<InvestmentDetailsTaxComplianceStatus, string> = {
	[InvestmentDetailsTaxComplianceStatus.REQUESTED]: 'dark.4',
	[InvestmentDetailsTaxComplianceStatus.SUBMITTED]: 'orange.8',
	[InvestmentDetailsTaxComplianceStatus.APPROVED]: 'teal.8',
	[InvestmentDetailsTaxComplianceStatus.REJECTED]: 'red.6',
};

interface Props {
	onOpenTaxCompliance: () => void;
	onManageForms?: () => void;
	canMutate: boolean;
}

const STATUS_TEXT_KEY: Record<InvestmentDetailsTaxComplianceStatus, string> = {
	[InvestmentDetailsTaxComplianceStatus.REQUESTED]: 'investment_tax_compliance.status.requested',
	[InvestmentDetailsTaxComplianceStatus.SUBMITTED]: 'investment_tax_compliance.status.submitted',
	[InvestmentDetailsTaxComplianceStatus.APPROVED]: 'investment_tax_compliance.status.approved',
	[InvestmentDetailsTaxComplianceStatus.REJECTED]: 'investment_tax_compliance.status.rejected',
};

const buildItemCardProps = (
	item: InvestmentDetailsTaxComplianceItem,
	tMipManage: TranslateFunction<I18Namespaces.MIP_MANAGE>
): InvestmentDocumentCardProps => {
	const { color, textColor } = resolveStatusBadgeColors(TAX_COMPLIANCE_STATUS_COLOR[item.status]);

	return {
		documentName: item.name,
		subtitle: item.subtitle ?? undefined,
		statusText: tMipManage(STATUS_TEXT_KEY[item.status]),
		statusColor: color,
		statusTextColor: textColor,
	};
};

export const InvestmentTaxCompliance = ({
	onOpenTaxCompliance,
	onManageForms,
	canMutate,
}: Props) => {
	const tMipManage = useTranslationMipManage();
	const { mipId } = useParams();
	const { data, isLoading } = useInvestmentDetailsTaxCompliance();

	const taxCompliance = data?.taxCompliance;

	const renderBody = () => {
		if (isLoading) {
			return <CardBodySkeleton count={2} rowHeight={48} mih={151} />;
		}

		if (taxCompliance?.length) {
			return (
				<Stack gap="0.5rem" p="0.5rem">
					{taxCompliance.map((item) => (
						<InvestmentDocumentCard
							key={item.id}
							{...buildItemCardProps(item, tMipManage)}
						/>
					))}
				</Stack>
			);
		}

		return (
			<InvestmentPlaceholder
				icon="document-v3"
				label={tMipManage('investment_tax_compliance.placeholder_label')}
				mih={151}
			/>
		);
	};

	return (
		<InvestmentDetailsCard
			cardLabel={tMipManage('investment_tax_compliance.card_label')}
			actions={
				<>
					{onManageForms && (
						<ManageRequirementsButton canMutate={canMutate} onClick={onManageForms}>
							{tMipManage('investment_kyc_status.button.manage_requirements')}
						</ManageRequirementsButton>
					)}
					<PermissionWrapper
						resourceType={ResourceType.PROGRAMME}
						resourceId={defaultToEmptyString(mipId)}
						permissionRole={PermissionRole.KYC_ADMIN}
					>
						<Button
							variant="default"
							rightSection={<Icon variant="arrow-up-right-2" size="sm" />}
							onClick={onOpenTaxCompliance}
						>
							{tMipManage('investment_tax_compliance.button.open_tax_compliance')}
						</Button>
					</PermissionWrapper>
				</>
			}
		>
			{renderBody()}
		</InvestmentDetailsCard>
	);
};
